<template>
  <hl-Modal dismisable @close="toggleEditInvoiceModal(false)">
    <!-- Loader -->
    <div v-if="loading.order" class="text-center py-5">
      <div class="spinner-grow text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <!-- / Loader -->
    <div v-else-if="order">
      <div class="mb-3">
        <h3 class="d-inline-block">Faktura</h3>
        <h5 class="d-inline-block ml-3 text-secondary">(utkast)</h5>

        <div class="gridContainer mt-5">
          <div class="gridScalable">
            <div class="gridScalable__flex">
              <span>Fakturanummer</span>
              <strong>(Blir tildelt ved utstedelse)</strong>
            </div>
            <div class="gridScalable__flex">
              <span>Fakturaperiode</span>
              <strong>
                {{ order.period }}
              </strong>
            </div>
          </div>

          <div class="gridScalable">
            <div class="gridScalable__flex">
              <span>Utstedelsesdato</span>
              <strong>
                <hl-Date :date="order.date.invoice" />
              </strong>
            </div>
            <div class="gridScalable__flex">
              <span>Forfallsdato</span>
              <strong>
                <hl-Date :date="order.date.due" />
              </strong>
            </div>
          </div>

          <div class="gridScalable">
            <div class="gridScalable__flex">
              <span>Kundenummer</span>
              <strong>
                {{ order.customerId }}
              </strong>
            </div>
            <div class="gridScalable__flex">
              <span>Bankkonto</span>
              <strong>
                {{ order.bankAccountNumber | formatBankAccount }}
              </strong>
            </div>
          </div>

          <div class="gridScalable">
            <div></div>
            <div class="gridScalable__flex">
              <span>KID</span>
              <strong>
                {{ order.structuredReference }}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-light mt-0 bg-absolute mt-4">
        <div class="orders">
          <span class="text-left">Beskrivelse</span>
          <span class="text-left">MVA</span>
          <span class="text-left">Enhetspris eks. MVA</span>
        </div>

        <div v-for="(row, rowIndex) in order.rows" :key="row.id" class="orders mt-2">
          <hl-Input size="sm" v-model="row.text" placeholder="Skriv inn beskrivelse" />
          <hl-Select
            v-if="row.isNew"
            size="sm"
            :hasMarginBottom="false"
            :value="row.vat"
            :options="MVAOptions"
            :disabled="MVAOptions.length <= 1"
            @input="setRowVAT(row, $event)"
          />
          <hl-Select
            v-else
            size="sm"
            :hasMarginBottom="false"
            :value="row.vat"
            :options="getRowVat(row)"
            :disabled="getRowVat(row).length <= 1"
            @input="setRowVAT(row, $event)"
          />
          <hl-Input type="number" size="sm" v-model="row.amount" />
          <button class="btn btn-sm remove-row">
            <i @click="removeRow(rowIndex)" class="budicon-trash"></i>
          </button>
        </div>

        <div class="add-row mt-4" @click="addRow">Legg til linje</div>

        <div class="gridContainer mt-4">
          <div class="gridScalable">
            <div class="d-flex justify-content-between">
              <span>Sum uten MVA</span>
              <hl-Currency :amount="calculatedSumWithoutVAT" />
            </div>
          </div>

          <div class="gridScalable">
            <div class="d-flex justify-content-between">
              <span>Sum MVA</span>
              <hl-Currency :amount="calculatedSumVAT" />
            </div>
          </div>

          <hr class="m-0 my-1" />

          <div class="gridScalable">
            <div class="d-flex justify-content-between">
              <strong>Total å betale</strong>
              <strong>
                <hl-Currency :amount="calculatedSumVAT" />
              </strong>
            </div>
          </div>
        </div>

        <div class="row mt-5 text-center">
          <hl-Button class="m-auto" variant="primary" @click="saveOrder" :disabled="saving" :loading="saving">
            Lagre
          </hl-Button>
        </div>
      </div>
    </div>
  </hl-Modal>
</template>

<script>
import { mapMutations, mapActions } from 'vuex';
import canLandlordChargeVAT from '@/mixins/tenancy/canLandlordChargeVAT';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';

export default {
  components: {
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Date': Date,
    'hl-Input': Input,
    'hl-Select': Select,
    'hl-Currency': Currency,
  },

  props: {
    landlordId: {
      type: [Number, String],
    },

    tenancyId: {
      type: [Number, String],
    },

    orderId: {
      type: [Number, String],
    },
  },

  mixins: [canLandlordChargeVAT],

  data() {
    return {
      order: null,
      loading: {
        order: false,
      },
      saving: false,
    };
  },

  computed: {
    orderRows() {
      return this.order.rows;
    },

    calculatedSumWithoutVAT() {
      let sum = 0;

      for (const row of this.order.rows) {
        if (row.amount == null) return sum;
        sum += parseInt(row.amount);
      }
      return sum;
    },

    calculatedSumVAT() {
      let sum = 0;

      for (const row of this.order.rows) {
        if (row.amount == null || row.vat == null) return sum;
        sum += (parseInt(row.amount) * row.vat) / 100;
      }

      return sum;
    },

    totalSum() {
      if (this.calculatedSumWithoutVAT == null || this.calculatedSumVAT == null) return;
      return this.calculatedSumWithoutVAT + this.calculatedSumVAT;
    },
  },

  methods: {
    ...mapMutations('tenancy', ['toggleEditInvoiceModal']),
    ...mapActions('tenancy', ['fetchTenancyOrder', 'updateTenancyOrderRows']),

    async fetchOrder() {
      try {
        this.loading.order = true;
        this.order = await this.fetchTenancyOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.orderId,
        });
        // we need to know which rows have 25% vat when, currently, the landlord is no longer eligible to use VAT
        this.order.rows.forEach(r => {
          r.isNew = false;
          r.unrestrictedVAT = r.vat === 25;
        });
      } catch (err) {
        console.error(err);
        this.$toast('Kunne ikke hente ordre.', 'error');
      }
      this.loading.order = false;
    },

    addRow() {
      this.order.rows.push({
        localId: this.order.rows.length,
        text: '',
        vat: 0,
        amount: null,
        isNew: true,
        unrestrictedVAT: this.canLandlordChargeVat,
      });
    },

    setRowVAT(row, vat) {
      row.vat = parseInt(vat, 10);
      // trigger reactivity of this.order.rows
      this.order.rows = [...this.order.rows];
    },

    getRowVat(row) {
      return this.MVAOptionsForRow(row);
    },

    removeRow(index) {
      this.order.rows.splice(index, 1);
    },

    async saveOrder() {
      this.saving = true;
      try {
        // order.rows[] will contain 2 extra fields needed for internal logic: 'new' and 'unrestrictedVAT', delete these
        this.order.rows.forEach(r => {
          delete r.isNew;
          delete r.unrestrictedVAT;
        });
        await this.updateTenancyOrderRows({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.orderId,
          rows: this.order.rows,
        });
        this.toggleEditInvoiceModal(false);
        this.$emit('order-saved');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre ordre.', 'error');
      }
      this.saving = false;
    },
  },

  mounted() {
    this.fetchOrder();
  },

  beforeDestroy() {
    // ensure that we destroy an unsaved invoice
    this.newInvoice = {};
  },
};
</script>

<style scoped lang="scss">
.bg-absolute {
  margin-left: -3rem;
  margin-bottom: -3rem;
  margin-top: -3rem;
  width: calc(100% + 6rem);
  padding: 2.3rem;
}

.add-row {
  width: 100%;
  padding: 1rem;
  background: $gray-100;
  text-align: center;
  border: 1px solid $gray-300;
  border-radius: $border-radius-medium;
  color: $gray-800;
  cursor: pointer;
}

.remove-row {
  background: white;
  border: 1px solid $gray-400;
  height: 31px;

  i {
    font-size: 1rem !important;
  }
}

.gridContainer {
  display: flex;
  flex-direction: column;
  gap: 0.3rem 0rem;
}

.gridScalable {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0rem 2rem;
  text-align: left;
}
.gridScalable__flex {
  display: flex;
  justify-content: space-between;
}
.gridThreeCols {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0rem 2rem;
  text-align: left;
}
.orders {
  display: grid;
  grid-template-columns: 1fr 0.3fr 1fr 0.1fr;
  gap: 0rem 1.5rem;
}
</style>
